import { Shimmer } from "@/components/ai-elements/shimmer";
import MapCompanies from "@/components/company/map-companies";
import DataTableCompanies from "@/components/data-table-companies/data-table-companies";
import DataTableCompanyEmployees from "@/components/data-table-company-employees/data-table-company-employees";
import ErrorMessage from "@/components/error-message";
import { MapControls } from "@/components/itinerary/map-controls";
import FeedbackSearchResult from "@/components/prospecting/feedback-search-result";
import InputSearchCompanies from "@/components/prospecting/input-search-companies";
import { RecentHistoryList } from "@/components/prospecting/recent-history-list";
import { SearchClarificationChips } from "@/components/prospecting/search-clarification-chips";
import { SearchQueriesAiSuggestions } from "@/components/prospecting/search-queries-ai-suggestions";
import { SearchReasoning } from "@/components/prospecting/search-reasoning";
import SearchEngineDebugModal from "@/components/prospecting/search-engine-debug-modal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Separator } from "@/components/ui/separator";
import { useApi } from "@/hooks/useApi";
import { useSearchPageState } from "@/hooks/useSearchPageState";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import NotFoundPage from "@/pages/shared/not-found";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { AlertTriangle, InfoIcon, SearchIcon } from "lucide-react";
import { isMobile } from "react-device-detect";
import { useState } from "react";
import { Trans } from "react-i18next";
import { RiRobot2Line } from "react-icons/ri";

export default function SearchPage() {
  const api = useApi();
  const { t } = useTranslation();
  const { isAdminMode } = useAdminMode();

  const {
    querySearchValue,
    searchCompaniesSelected,
    isLoadingSearch,
    isLoadingSavedSearch,
    notFound,
    countSearchLaunched,
    companies,
    employees,
    searchType,
    mainError,
    canSearchMore,
    maxResultsReached,
    forcedTool,
    setForcedTool,
    searchMode,
    setSearchMode,
    isMapOpened,
    setIsMapOpened,
    companySelected,
    setCompanySelected,
    itineraryState,
    setItineraryState,
    searchEngineResult,
    reasoningEvents,
    previousSearchAgents,
    savedFeedbackRating,
    savedFeedbackComment,
    pendingClarification,
    setPendingClarification,
    setSearchCompaniesSelected,
    currentSearchIdRef,
    personalizedCapabilities,
    isRelaunch,
    hasResults,
    launchNewSearch,
    relaunchSearch,
    cancelSearch,
    launchSearchMore,
    setAiSuggestions,
  } = useSearchPageState();

  const [isEngineDebugOpen, setIsEngineDebugOpen] = useState(false);

  if (notFound) {
    return <NotFoundPage />;
  }

  // "Search More" controls, shared by company and employee result views.
  // Gated on the active result type's count so it appears for both.
  const resultsCount =
    searchType === "employee" ? employees.length : companies.length;
  const searchMoreSection = !isLoadingSearch &&
    !isLoadingSavedSearch &&
    countSearchLaunched >= 1 &&
    resultsCount > 0 && (
      <>
        {canSearchMore && !maxResultsReached && (
          <p className="flex items-center justify-center gap-1.5 mt-4 text-xs text-center text-muted-foreground">
            <InfoIcon className="size-3.5 shrink-0" />
            {t("prospecting.search.helper.searchEngineInfo")}
          </p>
        )}

        {!canSearchMore && !maxResultsReached && countSearchLaunched >= 2 && (
          <p className="flex items-center justify-center gap-1.5 mt-4 text-xs text-center text-muted-foreground">
            <InfoIcon className="size-3.5 shrink-0" />
            {t("prospecting.search.helper.noMoreResults")}
          </p>
        )}

        {maxResultsReached && (
          <Alert variant="warning" className="mt-4">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              {t("prospecting.search.helper.maxResultsReached")}
            </AlertDescription>
          </Alert>
        )}

        {canSearchMore && !maxResultsReached && (
          <div className="flex justify-center mt-4">
            <button
              onClick={launchSearchMore}
              disabled={isLoadingSearch}
              className={cn(
                "relative inline-flex overflow-hidden rounded-full p-[2px] transition-all duration-300",
                isLoadingSearch
                  ? "shadow-xl shadow-purple-500/30 cursor-wait"
                  : "hover:-translate-y-0.5 hover:shadow-xl hover:shadow-purple-500/20",
              )}
            >
              <span
                className={cn(
                  "absolute inset-[-1000%] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]",
                  isLoadingSearch && "animate-[spin_2s_linear_infinite]",
                )}
              />
              <span className="relative flex items-center gap-2.5 rounded-full bg-white px-6 py-2.5 text-sm font-medium text-gray-700 backdrop-blur-3xl transition-shadow dark:bg-gray-900 dark:text-gray-200">
                <Avatar className="size-6 shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    <RiRobot2Line />
                  </AvatarFallback>
                </Avatar>
                <SearchIcon
                  className={cn("w-4 h-4", isLoadingSearch && "animate-pulse")}
                />
                {isLoadingSearch ? (
                  <Shimmer duration={2} spread={2}>
                    {t("prospecting.search.buttonSearching")}
                  </Shimmer>
                ) : (
                  t("prospecting.search.buttonMore")
                )}
              </span>
            </button>
          </div>
        )}
      </>
    );

  return (
    <>
      <ResizablePanelGroup orientation="horizontal" className="w-full">
        <ResizablePanel defaultSize="50%" className="relative">
          <div
            className={`overflow-auto absolute top-0 left-0 flex-1 pt-6 space-y-4 w-full h-full`}
          >
            <div className={`flex-1 p-8 pt-0 space-y-5`}>
              <div className="flex justify-between">
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold tracking-tight">
                    {t("prospecting.search.title")}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    <Trans
                      i18nKey="prospecting.search.subtitle"
                      components={{ br: <br /> }}
                    />
                  </p>
                </div>
                {isMobile === false && (
                  <div className="flex gap-2">
                    {isAdminMode && searchEngineResult && (
                      <>
                        <Button
                          variant="outline"
                          className="border-warning"
                          onClick={() => setIsEngineDebugOpen(true)}
                        >
                          {t("prospecting.search.engine.title")}
                        </Button>
                        <SearchEngineDebugModal
                          open={isEngineDebugOpen}
                          onOpenChange={setIsEngineDebugOpen}
                          searchId={currentSearchIdRef.current}
                          searchEngineResult={searchEngineResult}
                          isSearching={isLoadingSearch}
                        />
                      </>
                    )}
                    {searchType === "company" && (
                      <MapControls
                        companies={companies}
                        isMapOpened={isMapOpened}
                        onMapOpenChange={setIsMapOpened}
                        onItineraryStateChange={setItineraryState}
                      />
                    )}
                  </div>
                )}
              </div>

              <Separator className="my-6" />

              <div className="flex flex-col justify-center gap-2 pb-5">
                <InputSearchCompanies
                  selected={searchCompaniesSelected}
                  onLaunchNewSearch={launchNewSearch}
                  onRelaunch={relaunchSearch}
                  isLoading={isLoadingSearch}
                  isRelaunch={isRelaunch}
                  onCancel={cancelSearch}
                  forcedTool={forcedTool}
                  searchMode={searchMode}
                  personalizedCapabilities={personalizedCapabilities}
                  onForcedToolChange={(tool) => setForcedTool(tool || null)}
                  onSearchModeChange={setSearchMode}
                />
                {pendingClarification && (
                  <div className="flex justify-center">
                    <div className="w-full max-w-4xl">
                      <SearchClarificationChips
                        options={pendingClarification.options}
                        onPick={async (optionId) => {
                          const sid = pendingClarification.search_id;
                          const picked = pendingClarification.options.find(
                            (o) => o.id === optionId,
                          );
                          if (picked) {
                            setSearchCompaniesSelected({ text: picked.label });
                          }
                          setPendingClarification(null);
                          try {
                            await api.post("/search-engine/clarify", {
                              search_id: sid,
                              option_id: optionId,
                            });
                          } catch (error) {
                            const status = (
                              error as { response?: { status?: number } }
                            )?.response?.status;
                            if (status !== 404) {
                              console.warn(
                                "Failed to resolve clarification:",
                                error,
                              );
                            }
                          }
                        }}
                        onSkip={async () => {
                          const sid = pendingClarification.search_id;
                          setPendingClarification(null);
                          try {
                            await api.post("/search-engine/clarify", {
                              search_id: sid,
                              option_id: "skip",
                            });
                          } catch {
                            // Pending clarification may have already been
                            // resolved or aborted server-side — silent.
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
                {!pendingClarification &&
                  (isLoadingSearch || reasoningEvents.length > 0) && (
                  <SearchReasoning
                    reasoningEvents={reasoningEvents}
                    isStreaming={isLoadingSearch}
                    companiesCount={companies.length}
                    employeesCount={employees.length}
                    query={querySearchValue}
                    onReplay={relaunchSearch}
                    searchEngineResult={
                      searchEngineResult
                        ? {
                            ...searchEngineResult,
                            searchEngineAgents: [
                              ...previousSearchAgents,
                              ...searchEngineResult.searchEngineAgents,
                            ],
                          }
                        : undefined
                    }
                    rightContent={
                      countSearchLaunched >= 1 && companies.length > 0 ? (
                        <FeedbackSearchResult
                          inline
                          searchId={currentSearchIdRef.current}
                          query={querySearchValue}
                          initialRating={savedFeedbackRating}
                          initialComment={savedFeedbackComment}
                        />
                      ) : undefined
                    }
                  />
                )}
              </div>

              {countSearchLaunched === 0 &&
                !isLoadingSearch &&
                !isLoadingSavedSearch &&
                !mainError && (
                  <>
                    <div className="flex flex-wrap justify-center gap-2 pb-5">
                      <div className="w-full max-w-6xl">
                        <SearchQueriesAiSuggestions
                          onSelect={(query) => launchNewSearch({ text: query })}
                          onSuggestionsLoaded={setAiSuggestions}
                        />
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div className="w-full max-w-2xl">
                        <RecentHistoryList />
                      </div>
                    </div>
                  </>
                )}

              {countSearchLaunched === 0 && mainError && <ErrorMessage />}

              {hasResults && !pendingClarification && (
                <>
                  {searchType === "employee" ? (
                    <>
                      <DataTableCompanyEmployees
                        employees={employees}
                        isLoading={isLoadingSearch || isLoadingSavedSearch}
                        searchQuery={querySearchValue}
                        forceContactActions
                      />
                      {searchMoreSection}
                    </>
                  ) : (
                    <>
                      <DataTableCompanies
                        companies={companies}
                        selected={companySelected}
                        isLoading={isLoadingSearch || isLoadingSavedSearch}
                        pagination={false}
                        onSortedDataChange={itineraryState?.setSortedCompanies}
                        overrideColumnsVisibility={["Correspondance IA"]}
                        searchQuery={querySearchValue}
                      />
                      {searchMoreSection}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </ResizablePanel>
        {isMapOpened && searchType === "company" && (
          <>
            <ResizableHandle
              withHandle
              className="transition-all hover:bg-gray-500 active:bg-gray-200"
            />
            <ResizablePanel defaultSize="20%" maxSize="50%">
              <MapCompanies
                companies={itineraryState?.companiesForMap || companies}
                selected={companySelected}
                setSelected={setCompanySelected}
                shouldCalculateItinerary={
                  itineraryState?.shouldCalculateItinerary || false
                }
              />
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </>
  );
}
